/**
 * ClientImportDialog — bulk-create clients from a CSV upload.
 *
 * Flow: user picks/drops CSV → PapaParse reads it client-side → we normalise
 * the flexible column layout (keyword_1/2/3 → keywords array, pipe-separated
 * single cells → arrays, etc.) → preview table with per-row ✓/⚠ icons → user
 * confirms → POST /clients/import → results screen with created/skipped breakdown.
 *
 * The server does its own validation + duplicate-domain detection so the
 * preview is a hint, not a guarantee; "Domain already exists" is reported
 * post-import because we don't look at the DB until we hit the endpoint.
 */
import React, { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Upload, FileText, CheckCircle2, AlertTriangle, X, Download, Loader2,
  ArrowRight, FileWarning, PartyPopper,
} from "lucide-react";
import { downloadCsv } from "../lib/csv";

/* --------------------------------------------------------------------------
 * Column aliases — the sheet's humans may write "Client Name" or "name" or
 * "Name" or "client_name". We normalise everything to a canonical key.
 * -------------------------------------------------------------------------- */
const ALIAS_MAP = {
  name: ["name", "client_name", "client", "company", "company_name"],
  domain: ["domain", "website", "url", "domain_url", "website_url"],
  logo_url: ["logo_url", "logo", "logourl"],
  ga4_property_id: ["ga4_property_id", "ga4", "analytics_id", "analyticsid", "ga_id"],
  gbp_cid: ["gbp_cid", "gmb_cid", "cid", "google_business_cid"],
  gbp_business_name: ["gbp_business_name", "gmb_name", "business_name"],
  gsc_property_url: ["gsc_property_url", "gsc", "search_console"],
  campaign_type: ["campaign_type", "campaign", "type", "scope"],
  location_name: ["location_name", "location", "country"],
  service_location: ["service_location", "city", "service_area"],
  report_email: ["report_email", "report_emails", "email", "reporting_email"],
  client_first_name: ["client_first_name", "contact_first_name", "contact"],
  notes: ["notes", "note", "description"],
};

/* Multi-value column prefixes — `keyword_1`, `keyword_2`, …`competitor_12` etc.
 * matched case-insensitively. */
const MULTI_COLS = {
  keywords: ["keyword", "keywords", "kw"],
  competitors: ["competitor", "competitors", "comp"],
  ai_prompts: ["geo_prompt", "ai_prompt", "ai_prompts", "prompt", "prompts"],
};

const norm = (s) => (s == null ? "" : String(s)).trim().toLowerCase().replace(/\s+/g, "_");

/* Build a lookup from the actual CSV headers to canonical fields. We do this
 * once per upload so the per-row conversion stays O(cols). */
function buildHeaderMap(headers) {
  const map = {}; // canonical -> headerName
  const multi = {}; // canonical -> headerName[] (sorted by trailing number)
  const used = new Set();

  for (const [canonical, aliases] of Object.entries(ALIAS_MAP)) {
    const match = headers.find((h) => aliases.includes(norm(h)));
    if (match) {
      map[canonical] = match;
      used.add(match);
    }
  }

  for (const [canonical, prefixes] of Object.entries(MULTI_COLS)) {
    // Case 1: single column with that exact name
    const single = headers.find((h) => prefixes.includes(norm(h)) && !used.has(h));
    if (single) {
      map[canonical] = single;
      used.add(single);
    }
    // Case 2: numbered columns (keyword_1, keyword_2, …)
    const numbered = headers
      .filter((h) => !used.has(h) && prefixes.some((p) => {
        const n = norm(h);
        return n.startsWith(p + "_") || n.startsWith(p + " ");
      }))
      .map((h) => {
        const m = String(h).match(/(\d+)\s*$/);
        return { h, idx: m ? parseInt(m[1], 10) : 999 };
      })
      .sort((a, b) => a.idx - b.idx)
      .map((x) => x.h);
    if (numbered.length) {
      multi[canonical] = numbered;
      numbered.forEach((h) => used.add(h));
    }
  }

  return { map, multi, unused: headers.filter((h) => !used.has(h)) };
}

/* Pipe-separated OR newline-separated → array. Trim, drop empties. */
function splitMulti(cell) {
  if (!cell) return [];
  return String(cell)
    .split(/[|\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/* Strip surrounding whitespace AND any wrapping double-quote chars from a cell.
 * Belt-and-braces for CSVs that papa failed to unquote (weird exports, BOMs). */
function cleanCell(v) {
  if (v == null) return "";
  let s = String(v).trim();
  // Peel off wrapping quotes if present on BOTH sides.
  while (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

function normaliseRow(row, headerMap) {
  const { map, multi } = headerMap;
  const out = {};
  for (const [canonical, header] of Object.entries(map)) {
    const v = cleanCell(row[header]);
    if (v) out[canonical] = v;
  }
  // Multi — single pipe-separated column takes precedence IF present.
  for (const canonical of Object.keys(MULTI_COLS)) {
    const singleHeader = map[canonical];
    const numberedHeaders = multi[canonical] || [];
    const singleVal = singleHeader ? cleanCell(row[singleHeader]) : "";
    if (singleVal) {
      out[canonical] = splitMulti(singleVal);
    } else if (numberedHeaders.length) {
      out[canonical] = numberedHeaders.map((h) => cleanCell(row[h])).filter(Boolean);
    }
  }
  return out;
}

function validateRow(norm) {
  const errs = [];
  if (!norm.name) errs.push("Missing name");
  if (!norm.domain) errs.push("Missing domain");
  if (norm.campaign_type && !["national", "local"].includes(norm.campaign_type.toLowerCase())) {
    errs.push(`campaign_type "${norm.campaign_type}" must be "national" or "local"`);
  }
  return errs;
}

const TEMPLATE_HEADERS = [
  "name", "domain", "logo_url", "ga4_property_id", "gbp_cid",
  "campaign_type", "service_location", "location_name",
  "gsc_property_url", "report_email", "client_first_name", "notes",
  "keyword_1", "keyword_2", "keyword_3",
  "competitor_1", "competitor_2", "competitor_3",
  "geo_prompt_1", "geo_prompt_2", "geo_prompt_3",
];
const TEMPLATE_SAMPLE = [
  "ACME Aesthetics", "acme-aesthetics.co.uk", "https://cdn.acme.co.uk/logo.png",
  "123456789", "17891234567890123456",
  "local", "Bromsgrove,West Midlands,England,United Kingdom", "United Kingdom",
  "sc-domain:acme-aesthetics.co.uk", "reports@acme.co.uk", "Sarah",
  "First-month onboarding client.",
  "botox bromsgrove", "lip filler bromsgrove", "aesthetics bromsgrove",
  "theaestheticsroom.net", "ameliasaesthetics.co.uk", "",
  "What are the best aesthetic clinics in Bromsgrove?",
  "Best skin treatment near me in the Midlands",
  "Who offers lip filler in Worcestershire?",
];

function downloadTemplate() {
  const csv = [
    TEMPLATE_HEADERS.map((h) => `"${h}"`).join(","),
    TEMPLATE_SAMPLE.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
  ].join("\n");
  downloadCsv("rankos-client-import-template.csv", csv);
  toast.success("Template downloaded — fill in the second row and add more below.");
}

export default function ClientImportDialog({ open, onOpenChange, onImported }) {
  const [stage, setStage] = useState("upload"); // upload | preview | result
  const [filename, setFilename] = useState("");
  const [rows, setRows] = useState([]); // normalised + validated
  const [headerMap, setHeaderMap] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setStage("upload");
    setFilename("");
    setRows([]);
    setHeaderMap(null);
    setImporting(false);
    setResult(null);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const parseFile = (file) => {
    setFilename(file.name);
    // Read as text first so we can defensively strip line-leading whitespace
    // before Papa sees it — otherwise Papa treats a line like `   "foo","bar"`
    // as unquoted (per CSV spec) and keeps the literal `"` characters in the
    // field value. Users exporting from Excel/Numbers hit this surprisingly often.
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result || "")
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((l) => l.replace(/^[ \t]+/, ""))
        .join("\n");
      const { data, meta, errors } = Papa.parse(text, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (h) => h.trim(),
      });
      if (errors && errors.length) {
        // Non-fatal — Papa reports per-row issues, we show a toast and still
        // try to render what parsed cleanly.
        console.warn("CSV parse warnings:", errors);
      }
      if (!data || data.length === 0) {
        toast.error("CSV has no data rows.");
        return;
      }
      const headers = meta.fields || [];
      const map = buildHeaderMap(headers);
      setHeaderMap(map);
      if (!map.map.name || !map.map.domain) {
        toast.error("CSV must have at least a 'name' and 'domain' column.");
        return;
      }
      const normed = data.map((r, i) => {
        const n = normaliseRow(r, map);
        return { idx: i + 1, raw: r, data: n, errors: validateRow(n) };
      });
      setRows(normed);
      setStage("preview");
    };
    reader.onerror = () => toast.error("Couldn't read the file.");
    reader.readAsText(file);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);

  const runImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/clients/import", {
        clients: validRows.map((r) => r.data),
      });
      setResult(data);
      setStage("result");
      if (data.created_count > 0) {
        toast.success(`Imported ${data.created_count} client${data.created_count === 1 ? "" : "s"}.`);
        onImported?.(data);
      } else {
        toast.error("No clients imported — see details.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col" data-testid="client-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1a202c]">
            {stage === "upload" && "Import clients from CSV"}
            {stage === "preview" && `Preview — ${filename}`}
            {stage === "result" && "Import complete"}
          </DialogTitle>
        </DialogHeader>

        {/* ---------- Upload ---------- */}
        {stage === "upload" && (
          <div className="space-y-4 overflow-y-auto">
            <p className="text-sm text-[#718096]">
              Upload a CSV where each row is one client. Minimum required columns:
              {" "}<code className="font-mono-tight bg-[#edf2f7] px-1.5 py-0.5 rounded text-[11px]">name</code>
              {" "}+ <code className="font-mono-tight bg-[#edf2f7] px-1.5 py-0.5 rounded text-[11px]">domain</code>.
              Everything else is optional — if a cell is blank we'll fall back to sensible defaults.
            </p>

            <button
              onClick={downloadTemplate}
              data-testid="import-download-template"
              className="btn-ghost inline-flex items-center gap-2 text-xs"
            >
              <Download className="h-3.5 w-3.5" /> Download a template with sample row
            </button>

            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`block cursor-pointer rounded-xl border-2 border-dashed transition-all ${
                dragOver ? "border-[#4fd1c5] bg-[#4fd1c5]/5" : "border-[#e2e8f0] bg-[#f7fafc] hover:border-[#4fd1c5] hover:bg-[#4fd1c5]/5"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                data-testid="import-file-input"
                className="hidden"
              />
              <div className="px-6 py-10 flex flex-col items-center text-center gap-2">
                <Upload className="h-8 w-8 text-[#4fd1c5]" strokeWidth={1.8} />
                <div className="font-display text-base text-[#1a202c]">Drop your CSV here, or click to browse</div>
                <div className="text-[11px] text-[#a0aec0]">Up to 500 rows per import · UTF-8 encoding</div>
              </div>
            </label>

            <details className="rounded-xl border border-[#edf2f7] bg-[#f7fafc] px-4 py-3">
              <summary className="text-xs font-semibold text-[#4a5568] cursor-pointer select-none">
                Column reference & tips
              </summary>
              <div className="mt-3 text-[11px] text-[#4a5568] space-y-2 leading-relaxed">
                <div><strong>Required</strong>: <code>name</code>, <code>domain</code></div>
                <div><strong>Common optional</strong>: <code>logo_url</code>, <code>ga4_property_id</code>, <code>gbp_cid</code>, <code>campaign_type</code> (national|local), <code>service_location</code>, <code>report_email</code>, <code>client_first_name</code>, <code>notes</code>, <code>gsc_property_url</code>, <code>location_name</code></div>
                <div><strong>Multi-value fields</strong> — use either style:
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Numbered columns: <code>keyword_1</code>, <code>keyword_2</code>, <code>keyword_3</code>…</li>
                    <li>OR a single column with pipe-separated values: <code>keyword 1 | keyword 2 | keyword 3</code></li>
                  </ul>
                </div>
                <div>Multi-value canonical names: <code>keyword(s)</code>, <code>competitor(s)</code>, <code>geo_prompt</code> (or <code>ai_prompt</code>)</div>
                <div><strong>Defaults applied</strong>: <code>location_name</code> → United Kingdom; <code>language_name</code> → English; <code>campaign_type</code> → national (or local if <code>service_location</code> is set); AI platforms → ChatGPT + Claude + Gemini + Perplexity when any prompts are present</div>
                <div><strong>Shared domains</strong> are fine — e.g. one client per branch of a multi-location business. Towns are matched to DataForSEO locations on import; any substitutions are listed afterwards.</div>
              </div>
            </details>
          </div>
        )}

        {/* ---------- Preview ---------- */}
        {stage === "preview" && (
          <div className="space-y-3 overflow-y-auto">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-[#4a5568]">
                Parsed <strong>{rows.length}</strong> row{rows.length === 1 ? "" : "s"} ·{" "}
                <span className="text-[#2c7a7b] font-semibold">{validRows.length} ready</span>
                {rows.length - validRows.length > 0 && (
                  <> · <span className="text-[#c05621] font-semibold">{rows.length - validRows.length} with errors</span></>
                )}
              </div>
              <button
                onClick={reset}
                className="btn-ghost inline-flex items-center gap-2 text-xs"
                data-testid="import-reupload"
              >
                <X className="h-3 w-3" /> Choose a different file
              </button>
            </div>

            {headerMap?.unused?.length > 0 && (
              <div className="rounded-xl border border-[#fed7aa] bg-[#fff7ed] text-[#9a3412] text-[11px] px-3 py-2">
                Columns ignored (not recognised): <span className="font-mono-tight">{headerMap.unused.join(", ")}</span>
              </div>
            )}

            <div className="rounded-xl border border-[#edf2f7] overflow-hidden" data-testid="import-preview-table">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold bg-[#f7fafc] border-b border-[#edf2f7]">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Domain</div>
                <div className="col-span-2">Scope</div>
                <div className="col-span-1 text-right">Kw</div>
                <div className="col-span-1 text-right">Comp</div>
                <div className="col-span-1 text-right">Prompts</div>
              </div>
              <div className="divide-y divide-[#edf2f7] max-h-[320px] overflow-y-auto">
                {rows.map((r) => {
                  const ok = r.errors.length === 0;
                  return (
                    <div
                      key={r.idx}
                      data-testid={`import-preview-row-${r.idx}`}
                      className={`grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center ${ok ? "" : "bg-[#fffaf0]"}`}
                    >
                      <div className="col-span-1 flex items-center justify-center">
                        {ok ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#2c7a7b]" />
                        ) : (
                          <AlertTriangle className="h-3.5 w-3.5 text-[#c05621]" />
                        )}
                      </div>
                      <div className="col-span-3 truncate font-semibold text-[#1a202c]" title={r.data.name || "(blank)"}>
                        {r.data.name || <span className="text-[#c05621] italic">missing</span>}
                      </div>
                      <div className="col-span-3 truncate font-mono-tight text-[#4a5568]" title={r.data.domain || "(blank)"}>
                        {r.data.domain || <span className="text-[#c05621] italic">missing</span>}
                      </div>
                      <div className="col-span-2 text-[11px] text-[#4a5568] font-mono-tight">
                        {r.data.campaign_type || (r.data.service_location ? "local*" : "national*")}
                        {r.data.service_location && <span className="text-[#a0aec0]"> · {r.data.service_location.split(",")[0]}</span>}
                      </div>
                      <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">{(r.data.keywords || []).length}</div>
                      <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">{(r.data.competitors || []).length}</div>
                      <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">{(r.data.ai_prompts || []).length}</div>
                      {!ok && (
                        <div className="col-span-12 text-[11px] text-[#c05621] mt-1 pl-[8%]">
                          {r.errors.join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ---------- Result ---------- */}
        {stage === "result" && result && (
          <div className="space-y-4 overflow-y-auto">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#4fd1c5]/10 flex items-center justify-center">
                <PartyPopper className="h-5 w-5 text-[#2c7a7b]" />
              </div>
              <div>
                <div className="font-display text-lg text-[#1a202c]">
                  {result.created_count} client{result.created_count === 1 ? "" : "s"} created
                </div>
                <div className="text-xs text-[#718096]">
                  {result.skipped_count > 0
                    ? `${result.skipped_count} skipped — details below.`
                    : "Every row made it in — nice CSV."}
                </div>
              </div>
            </div>

            {result.country_fixed_count > 0 && (
              <div className="text-[11px] text-[#2b6cb0]" data-testid="import-result-country-fixed">
                {result.country_fixed_count} row{result.country_fixed_count === 1 ? "" : "s"} had a town in <code>location_name</code> — moved to the service town and set the country to United Kingdom.
              </div>
            )}

            {result.adjusted?.length > 0 && (
              <div className="rounded-xl border border-[#bee3f8] bg-[#ebf8ff] overflow-hidden" data-testid="import-result-adjusted">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#2b6cb0] font-bold bg-[#bee3f8]/60">
                  Towns adjusted on import · {result.adjusted.length}
                </div>
                <div className="divide-y divide-[#bee3f8] max-h-[200px] overflow-y-auto">
                  {result.adjusted.map((a) => (
                    <div key={a.row} data-testid={`import-result-adjusted-${a.row}`} className="px-3 py-2 text-xs text-[#4a5568]">
                      <span className="font-mono-tight text-[#718096]">Row {a.row}</span> · <span className="font-semibold text-[#1a202c]">{a.name}</span>
                      {a.notes.map((n, i) => (
                        <div key={i} className="text-[11px] text-[#2b6cb0] mt-0.5">{n}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped_count > 0 && (
              <div className="rounded-xl border border-[#feebc8] bg-[#fffaf0] overflow-hidden">
                <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#744210] font-bold bg-[#fef3c7]">
                  Skipped rows
                </div>
                <div className="divide-y divide-[#feebc8] max-h-[200px] overflow-y-auto">
                  {result.skipped.map((s, i) => (
                    <div
                      key={i}
                      data-testid={`import-result-skipped-${s.row}`}
                      className="px-3 py-2 text-xs text-[#4a5568] flex items-start gap-2"
                    >
                      <FileWarning className="h-3 w-3 text-[#c05621] mt-0.5 flex-none" />
                      <div className="flex-1 min-w-0">
                        <span className="font-mono-tight text-[#718096]">Row {s.row}</span>
                        {s.name && <> · <span className="font-semibold text-[#1a202c]">{s.name}</span></>}
                        {s.domain && <> · <span className="font-mono-tight">{s.domain}</span></>}
                        <div className="text-[11px] text-[#c05621] mt-0.5">{s.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="pt-4 mt-2 gap-2 border-t border-[#e2e8f0] shrink-0">
          {stage === "upload" && (
            <button onClick={() => handleClose(false)} className="btn-ghost" data-testid="import-cancel">
              Cancel
            </button>
          )}
          {stage === "preview" && (
            <>
              <button onClick={() => handleClose(false)} className="btn-ghost" disabled={importing} data-testid="import-cancel">
                Cancel
              </button>
              <button
                onClick={runImport}
                disabled={importing || validRows.length === 0}
                data-testid="import-confirm"
                className="btn-primary inline-flex items-center gap-2"
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                {importing ? "Importing…" : `Import ${validRows.length} client${validRows.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
          {stage === "result" && (
            <button
              onClick={() => handleClose(false)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="import-done"
            >
              Done
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
